import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { apiService, ChatMessage } from '../services/api';
import { Button } from '../components/ui/button';
import { 
  Trash2, 
  Send, 
  Sparkles, 
  ExternalLink, 
  MessageSquare,
  TrendingUp,
  PiggyBank,
  Receipt,
  HelpCircle,
  Bot,
  User
} from 'lucide-react';

const suggestedQuestions = [
  { 
    text: "What were my biggest expenses last month?", 
    icon: TrendingUp,
    color: 'var(--theme-expense)'
  },
  { 
    text: "How much did I spend on groceries?", 
    icon: Receipt,
    color: 'var(--theme-primary)'
  },
  { 
    text: "What are my income sources?", 
    icon: PiggyBank,
    color: 'var(--theme-income)'
  },
  { 
    text: "Tell me about RRSP contributions", 
    icon: HelpCircle,
    color: 'var(--theme-accent)'
  },
];

export const AltoAIPage: React.FC = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [hasAccounts, setHasAccounts] = useState<boolean | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const shouldAutoSendRef = useRef(false);

  useEffect(() => {
    const abortController = new AbortController();
    loadChatHistory(abortController.signal);

    // Check if user has connected accounts
    const checkAccounts = async () => {
      try {
        const accountsResponse = await apiService.getAccounts(abortController.signal);
        setHasAccounts(accountsResponse.accounts && accountsResponse.accounts.length > 0);
      } catch (error) {
        console.error('Failed to check accounts:', error);
        setHasAccounts(false);
      }
    };
    checkAccounts();

    return () => abortController.abort();
  }, []);
  
  useEffect(() => {
    const initialMessage = localStorage.getItem('alto_ai_initial_message');
    if (initialMessage) {
      localStorage.removeItem('alto_ai_initial_message');
      setInputMessage(initialMessage);
      shouldAutoSendRef.current = true;
    }
  }, []);
  
  useEffect(() => {
    if (shouldAutoSendRef.current && inputMessage && !isLoading && !isSending) {
      shouldAutoSendRef.current = false;
      const timer = setTimeout(() => {
        handleSendMessage();
      }, 200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputMessage, isLoading, isSending]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const response = await apiService.getChatHistory(signal);
      if (!signal?.aborted) {
        setMessages(response.messages);
      }
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Failed to load chat history:', error);
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setIsSending(true);
    setStreamingMessage('');

    const tempUserMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      user_id: '',
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);

    try {
      // Check if user has connected bank accounts
      const accountsResponse = await apiService.getAccounts();
      const hasAccounts = accountsResponse.accounts && accountsResponse.accounts.length > 0;

      if (!hasAccounts) {
        // Provide canned response for users without connected accounts
        const cannedResponse = `I'm Alto AI, your personal finance assistant powered by advanced AI technology. I can help you understand your spending patterns, analyze income sources, track financial goals, and provide personalized insights to improve your financial health.

Here are some key things I can help you with:

• **Spending Analysis**: Track and categorize your expenses, identify spending trends, and find opportunities to save money
• **Income Insights**: Monitor your income sources, analyze cash flow, and optimize your earnings
• **Financial Planning**: Set and track savings goals, create budgets, and get personalized recommendations
• **Transaction Intelligence**: Get AI-powered categorization of your transactions and smart alerts

To get started and unlock all these features, you'll need to connect a bank account first so I can access your financial data. Click here to [connect your bank account](/accounts) and start getting personalized financial insights!`;

        // Simulate streaming the response
        const words = cannedResponse.split(' ');
        let currentText = '';

        for (let i = 0; i < words.length; i++) {
          currentText += (i > 0 ? ' ' : '') + words[i];
          setStreamingMessage(currentText);
          await new Promise(resolve => setTimeout(resolve, 50)); // Simulate typing speed
        }

        // Add the final message to chat history
        const aiMessage: ChatMessage = {
          id: `ai-${Date.now()}`,
          user_id: '',
          role: 'assistant',
          content: cannedResponse,
          created_at: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, aiMessage]);
        setStreamingMessage('');
        setIsSending(false);
      } else {
        // User has connected accounts, proceed with normal AI chat
        await apiService.sendChatMessage(
          userMessage,
          (chunk) => {
            if (chunk.type === 'content') {
              setStreamingMessage((prev) => prev + chunk.content);
            } else if (chunk.type === 'tool_call') {
              console.log('Tool called:', chunk.tool_name, chunk.tool_args);
            } else if (chunk.type === 'error') {
              console.error('Stream error:', chunk.content);
            }
          },
          (error) => {
            console.error('Error sending message:', error);
            setIsSending(false);
          },
          () => {
            setStreamingMessage('');
            setIsSending(false);
            loadChatHistory();
          }
        );
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setIsSending(false);
      setStreamingMessage('');
    }
  };

  const handleClearHistory = async () => {
    try {
      await apiService.clearChatHistory();
      setMessages([]);
      setShowClearModal(false);
    } catch (error) {
      console.error('Failed to clear chat history:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const TransactionLink = ({ href, children }: { href?: string; children?: React.ReactNode }) => {
    if (!href) return <>{children}</>;
    
    if (href.startsWith('/transactions')) {
      return (
        <a
          href={href}
          onClick={(e) => {
            e.preventDefault();
            navigate(href);
          }}
          className="inline-flex items-center gap-1 font-medium transition-colors cursor-pointer no-underline"
          style={{ color: 'var(--theme-primary)' }}
        >
          {children}
          <ExternalLink className="h-3.5 w-3.5 inline" />
        </a>
      );
    }
    
    return (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer" 
        style={{ color: 'var(--theme-primary)' }}
      >
        {children}
      </a>
    );
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [inputMessage]);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-full flex flex-col" style={{ backgroundColor: 'var(--theme-background)' }}>
      {/* Header */}
      <div 
        className="px-4 py-4 md:px-8 md:py-5 flex-shrink-0"
        style={{ 
          backgroundColor: 'var(--theme-surface)',
          borderBottom: '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)'
        }}
      >
        <div className="max-w-4xl mx-auto flex justify-between items-center gap-3">
          <div className="flex items-center gap-3">
            <div 
              className="p-2.5 rounded-2xl"
              style={{
                background: `linear-gradient(135deg, var(--theme-primary) 0%, color-mix(in srgb, var(--theme-primary) 70%, #000) 100%)`
              }}
            >
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 
                className="text-lg md:text-xl font-bold tracking-tight"
                style={{ color: 'var(--theme-text)' }}
              >
                Alto AI
              </h1>
              <p 
                className="text-xs hidden sm:block"
                style={{ color: 'var(--theme-text-secondary)' }}
              >
                Your personal finance assistant
              </p>
            </div>
          </div>
          
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowClearModal(true)}
              className="gap-2 rounded-xl h-9"
              style={{ color: 'var(--theme-text-secondary)' }}
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden sm:inline">Clear</span>
            </Button>
          )}
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 md:px-8 space-y-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div 
                className="w-10 h-10 border-3 rounded-full animate-spin mb-4"
                style={{ 
                  borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                  borderTopColor: 'var(--theme-primary)'
                }}
              />
              <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
                Loading conversation...
              </p>
            </div>
          ) : messages.length === 0 && !streamingMessage ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center py-12 md:py-20 text-center px-4 fade-in-up">
              {/* Hero Icon */}
              <div 
                className="relative mb-6"
              >
                <div 
                  className="w-20 h-20 md:w-24 md:h-24 rounded-3xl flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, var(--theme-primary) 0%, color-mix(in srgb, var(--theme-primary) 60%, var(--theme-accent)) 100%)`
                  }}
                >
                  <Sparkles className="h-10 w-10 md:h-12 md:w-12 text-white" />
                </div>
                {/* Decorative rings */}
                <div 
                  className="absolute inset-0 rounded-3xl animate-ping opacity-20"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                />
              </div>
              
              <h2 
                className="text-2xl md:text-3xl font-bold tracking-tight mb-2"
                style={{ color: 'var(--theme-text)' }}
              >
                Welcome to Alto AI
              </h2>
              <p 
                className="text-sm md:text-base max-w-md mb-8"
                style={{ color: 'var(--theme-text-secondary)' }}
              >
                I can help you analyze spending, understand your income, and provide personalized financial insights.
              </p>
              
              {/* Suggested Questions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl">
                {suggestedQuestions.map((question, index) => {
                  const IconComponent = question.icon;
                  const isDisabled = hasAccounts === false;
                  return (
                    <button
                      key={index}
                      onClick={() => !isDisabled && setInputMessage(question.text)}
                      disabled={isDisabled}
                      className={`group flex items-start gap-3 p-4 rounded-2xl text-left transition-all duration-200 ${
                        isDisabled
                          ? 'cursor-not-allowed opacity-60'
                          : 'hover:scale-[1.02] active:scale-[0.98]'
                      }`}
                      style={{
                        backgroundColor: 'var(--theme-surface)',
                        border: '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)'
                      }}
                    >
                      <div
                        className={`p-2 rounded-xl flex-shrink-0 transition-transform ${
                          isDisabled ? '' : 'group-hover:scale-110'
                        }`}
                        style={{
                          backgroundColor: `color-mix(in srgb, ${question.color} 12%, transparent)`,
                          opacity: isDisabled ? 0.5 : 1
                        }}
                      >
                        <IconComponent className="h-4 w-4" style={{ color: question.color }} />
                      </div>
                      <span
                        className="text-sm font-medium leading-relaxed"
                        style={{
                          color: isDisabled ? 'var(--theme-text-secondary)' : 'var(--theme-text)',
                          opacity: isDisabled ? 0.7 : 1
                        }}
                      >
                        {question.text}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Chat Messages */
            <>
              {messages.map((message, index) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'} fade-in-up`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* AI Avatar */}
                  {message.role === 'assistant' && (
                    <div 
                      className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mt-1"
                      style={{
                        background: `linear-gradient(135deg, var(--theme-primary) 0%, color-mix(in srgb, var(--theme-primary) 70%, #000) 100%)`
                      }}
                    >
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                  )}
                  
                  <div
                    className={`max-w-[85%] md:max-w-[75%] ${message.role === 'user' ? 'order-first' : ''}`}
                  >
                    <div
                      className="rounded-2xl px-4 py-3"
                      style={
                        message.role === 'user'
                          ? {
                              backgroundColor: 'var(--theme-primary)',
                              color: 'white',
                              borderBottomRightRadius: '6px'
                            }
                          : {
                              backgroundColor: 'var(--theme-surface)',
                              color: 'var(--theme-text)',
                              border: '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)',
                              borderBottomLeftRadius: '6px'
                            }
                      }
                    >
                      {message.role === 'user' ? (
                        <p className="whitespace-pre-wrap break-words text-sm md:text-base">{message.content}</p>
                      ) : (
                        <div className="prose prose-sm max-w-none text-sm md:text-base">
                          <ReactMarkdown
                            components={{
                              p: ({ children }) => <p className="mb-2 last:mb-0" style={{ color: 'var(--theme-text)' }}>{children}</p>,
                              ul: ({ children }) => <ul className="mb-2 ml-4 list-disc">{children}</ul>,
                              ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal">{children}</ol>,
                              li: ({ children }) => <li className="mb-1" style={{ color: 'var(--theme-text)' }}>{children}</li>,
                              code: ({ inline, children, ...props }: any) =>
                                inline ? (
                                  <code 
                                    className="px-1.5 py-0.5 rounded text-xs md:text-sm font-mono" 
                                    style={{ 
                                      backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)',
                                      color: 'var(--theme-text)'
                                    }}
                                    {...props}
                                  >
                                    {children}
                                  </code>
                                ) : (
                                  <code 
                                    className="block p-3 rounded-xl text-xs md:text-sm font-mono overflow-x-auto" 
                                    style={{ 
                                      backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)',
                                      color: 'var(--theme-text)'
                                    }}
                                    {...props}
                                  >
                                    {children}
                                  </code>
                                ),
                              strong: ({ children }) => <strong className="font-semibold" style={{ color: 'var(--theme-text)' }}>{children}</strong>,
                              em: ({ children }) => <em className="italic">{children}</em>,
                              h1: ({ children }) => <h1 className="text-lg md:text-xl font-bold mb-2" style={{ color: 'var(--theme-text)' }}>{children}</h1>,
                              h2: ({ children }) => <h2 className="text-base md:text-lg font-bold mb-2" style={{ color: 'var(--theme-text)' }}>{children}</h2>,
                              h3: ({ children }) => <h3 className="text-sm md:text-base font-bold mb-2" style={{ color: 'var(--theme-text)' }}>{children}</h3>,
                              blockquote: ({ children }) => (
                                <blockquote 
                                  className="border-l-4 pl-4 italic my-2"
                                  style={{ 
                                    borderColor: 'var(--theme-primary)',
                                    color: 'var(--theme-text-secondary)'
                                  }}
                                >
                                  {children}
                                </blockquote>
                              ),
                              a: ({ href, children }) => <TransactionLink href={href}>{children}</TransactionLink>,
                            }}
                          >
                            {message.content}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                    {message.created_at && (
                      <p 
                        className={`text-[10px] mt-1 ${message.role === 'user' ? 'text-right' : 'text-left'}`}
                        style={{ color: 'var(--theme-text-secondary)', opacity: 0.7 }}
                      >
                        {formatTime(message.created_at)}
                      </p>
                    )}
                  </div>
                  
                  {/* User Avatar */}
                  {message.role === 'user' && (
                    <div 
                      className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mt-1"
                      style={{ backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)' }}
                    >
                      <User className="h-4 w-4" style={{ color: 'var(--theme-text-secondary)' }} />
                    </div>
                  )}
                </div>
              ))}
              
              {/* Streaming Message */}
              {streamingMessage && (
                <div className="flex gap-3 justify-start fade-in-up">
                  <div 
                    className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mt-1"
                    style={{
                      background: `linear-gradient(135deg, var(--theme-primary) 0%, color-mix(in srgb, var(--theme-primary) 70%, #000) 100%)`
                    }}
                  >
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div className="max-w-[85%] md:max-w-[75%]">
                    <div
                      className="rounded-2xl px-4 py-3"
                      style={{
                        backgroundColor: 'var(--theme-surface)',
                        color: 'var(--theme-text)',
                        border: '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)',
                        borderBottomLeftRadius: '6px'
                      }}
                    >
                      <div className="prose prose-sm max-w-none text-sm md:text-base">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0" style={{ color: 'var(--theme-text)' }}>{children}</p>,
                            ul: ({ children }) => <ul className="mb-2 ml-4 list-disc">{children}</ul>,
                            ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal">{children}</ol>,
                            li: ({ children }) => <li className="mb-1" style={{ color: 'var(--theme-text)' }}>{children}</li>,
                            code: ({ inline, children, ...props }: any) =>
                              inline ? (
                                <code 
                                  className="px-1.5 py-0.5 rounded text-xs md:text-sm font-mono" 
                                  style={{ 
                                    backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)',
                                    color: 'var(--theme-text)'
                                  }}
                                  {...props}
                                >
                                  {children}
                                </code>
                              ) : (
                                <code 
                                  className="block p-3 rounded-xl text-xs md:text-sm font-mono overflow-x-auto" 
                                  style={{ 
                                    backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)',
                                    color: 'var(--theme-text)'
                                  }}
                                  {...props}
                                >
                                  {children}
                                </code>
                              ),
                            strong: ({ children }) => <strong className="font-semibold" style={{ color: 'var(--theme-text)' }}>{children}</strong>,
                            em: ({ children }) => <em className="italic">{children}</em>,
                            a: ({ href, children }) => <TransactionLink href={href}>{children}</TransactionLink>,
                          }}
                        >
                          {streamingMessage}
                        </ReactMarkdown>
                      </div>
                      {/* Typing indicator */}
                      <div className="flex gap-1 mt-2">
                        <span 
                          className="w-2 h-2 rounded-full animate-bounce"
                          style={{ backgroundColor: 'var(--theme-primary)', animationDelay: '0ms' }}
                        />
                        <span 
                          className="w-2 h-2 rounded-full animate-bounce"
                          style={{ backgroundColor: 'var(--theme-primary)', animationDelay: '150ms' }}
                        />
                        <span 
                          className="w-2 h-2 rounded-full animate-bounce"
                          style={{ backgroundColor: 'var(--theme-primary)', animationDelay: '300ms' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Thinking indicator when sending but no stream yet */}
              {isSending && !streamingMessage && (
                <div className="flex gap-3 justify-start fade-in-up">
                  <div 
                    className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, var(--theme-primary) 0%, color-mix(in srgb, var(--theme-primary) 70%, #000) 100%)`
                    }}
                  >
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div 
                    className="rounded-2xl px-4 py-3 flex items-center gap-2"
                    style={{
                      backgroundColor: 'var(--theme-surface)',
                      border: '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)',
                      borderBottomLeftRadius: '6px'
                    }}
                  >
                    <div className="flex gap-1">
                      <span 
                        className="w-2 h-2 rounded-full animate-bounce"
                        style={{ backgroundColor: 'var(--theme-primary)', animationDelay: '0ms' }}
                      />
                      <span 
                        className="w-2 h-2 rounded-full animate-bounce"
                        style={{ backgroundColor: 'var(--theme-primary)', animationDelay: '150ms' }}
                      />
                      <span 
                        className="w-2 h-2 rounded-full animate-bounce"
                        style={{ backgroundColor: 'var(--theme-primary)', animationDelay: '300ms' }}
                      />
                    </div>
                    <span 
                      className="text-sm"
                      style={{ color: 'var(--theme-text-secondary)' }}
                    >
                      Thinking...
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div 
        className="flex-shrink-0 px-4 py-4 md:px-8"
        style={{ 
          backgroundColor: 'var(--theme-surface)',
          borderTop: '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)'
        }}
      >
        <div className="max-w-4xl mx-auto">
          <div 
            className="flex items-end gap-3 p-2 rounded-2xl"
            style={{ 
              backgroundColor: 'var(--theme-background)',
              border: '1px solid color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)'
            }}
          >
            <div 
              className="p-2 rounded-xl flex-shrink-0 hidden sm:flex"
              style={{ backgroundColor: 'color-mix(in srgb, var(--theme-primary) 10%, transparent)' }}
            >
              <MessageSquare className="h-5 w-5" style={{ color: 'var(--theme-primary)' }} />
            </div>
            
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                hasAccounts === false
                  ? "Connect a bank account to start chatting..."
                  : "Ask me about your finances..."
              }
              disabled={isSending || hasAccounts === false}
              rows={1}
              className="flex-1 resize-none bg-transparent border-none outline-none text-sm md:text-base py-2 max-h-28 overflow-y-auto"
              style={{
                color: hasAccounts === false ? 'var(--theme-text-secondary)' : 'var(--theme-text)',
                caretColor: hasAccounts === false ? 'transparent' : 'var(--theme-primary)',
                opacity: hasAccounts === false ? 0.7 : 1
              }}
            />
            
            <Button
              data-send-button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isSending || hasAccounts === false}
              className="h-10 w-10 p-0 rounded-xl flex-shrink-0 transition-all disabled:opacity-40"
              style={{ 
                backgroundColor: 'var(--theme-primary)',
                color: 'white'
              }}
            >
              {isSending ? (
                <div 
                  className="w-5 h-5 border-2 rounded-full animate-spin"
                  style={{ 
                    borderColor: 'rgba(255,255,255,0.3)',
                    borderTopColor: 'white'
                  }}
                />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
          
          <p 
            className="text-[10px] text-center mt-2"
            style={{ color: 'var(--theme-text-secondary)', opacity: 0.7 }}
          >
            Alto AI can make mistakes. Always verify important financial information.
          </p>
        </div>
      </div>

      {/* Clear History Modal */}
      {showClearModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowClearModal(false)}
        >
          <div 
            className="max-w-sm w-full p-6 rounded-3xl shadow-2xl fade-in-up"
            style={{ backgroundColor: 'var(--theme-surface)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: 'color-mix(in srgb, var(--theme-expense) 12%, transparent)' }}
            >
              <Trash2 className="h-6 w-6" style={{ color: 'var(--theme-expense)' }} />
            </div>
            
            <h3 
              className="text-lg font-bold text-center mb-2"
              style={{ color: 'var(--theme-text)' }}
            >
              Clear Chat History?
            </h3>
            <p 
              className="text-sm text-center mb-6"
              style={{ color: 'var(--theme-text-secondary)' }}
            >
              This will permanently delete all your messages. This action cannot be undone.
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 h-11 rounded-xl font-medium"
                onClick={() => setShowClearModal(false)}
                style={{ 
                  backgroundColor: 'transparent',
                  borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                  color: 'var(--theme-text)'
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-11 rounded-xl font-medium"
                onClick={handleClearHistory}
                style={{ 
                  backgroundColor: 'var(--theme-expense)',
                  color: 'white'
                }}
              >
                Clear All
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
